import { Map, OrderedSet, Set } from 'immutable';

import { NotebookTypes } from '@mode/notebooks/contract';
import { NoResourceError, NotebookCellResource } from '@mode/legacy/data-access-model-js';
import { AppState, NormalizedNotebookCellArray } from '@mode/legacy/types-app-state';
import { ReducerTree } from '@mode/legacy/util-domain';
import { Actions } from '../../../actions/index';
import { CellRunActions } from '../../../actions/notebooks/cell-run.actions';
import { CellActions } from '../../../actions/notebooks/cell.actions';
import { NotebookActions } from '../../../actions/notebooks/notebook.actions';

export const initialCellsState: NormalizedNotebookCellArray = {
  tokens: OrderedSet<string>(),
  contents: Map<string, string>(),
  frozen: Map<string, boolean>(),
  folded: Map<string, boolean>(),
  contentTypes: Map<string, NotebookTypes.ContentType>(),
  createdAt: Map<string, Date>(),
  resources: Map<string, NotebookCellResource>(),
  executing: Map<string, boolean>(),
  lastRunTokens: Map<string, string>(),
  missingRuns: Set<string>(),
};

function tokensReducer(state = initialCellsState.tokens, action: Actions): NormalizedNotebookCellArray['tokens'] {
  switch (action.type) {
    case CellActions.Type.AddCellSuccess:
      // just in case, but may be unnecssary to maintain order here when the notebook has to maintain an ordered set as well
      if (action.nextToken) {
        const arr = state.toArray();
        arr.splice(arr.indexOf(action.nextToken), 0, action.cellResource.token);
        return OrderedSet(arr);
      }
      return state.add(action.cellResource.token);
    case CellActions.Type.DestroyCell:
      return state.remove(action.cellToken);
    case CellActions.Type.DestroyCellFailure:
      return state.add(action.cellToken);
    case CellActions.Type.LoadVizCellSuccess:
      return state.add(action.cellToken);
    case CellActions.Type.LoadNotebookCellsSuccess:
      // LoadNotebookCells is the full notebook, so it should override any existing cell ordering
      const tokens = action.cellResources.map((r) => r.token);
      return state.filterNot((t) => tokens.indexOf(t) >= 0).merge(tokens);
    case CellActions.Type.MoveCell: {
      // not really sure if this will always work, since it's assuming tokens in the same notebook will be groups in this set
      // since notebookCellTokens really keeps the order, probably could just remove all ordering functionality from here.
      const arr = state.toArray();
      const index = arr.indexOf(action.cellToken);
      if (index >= 0) {
        arr.splice(index, 1);
        if (action.down) {
          arr.splice(index + 1, 0, action.cellToken);
        } else {
          arr.splice(index - 1 >= 0 ? index - 1 : 0, 0, action.cellToken);
        }

        return OrderedSet(arr);
      }
      return state;
    }
  }

  return state;
}

function contentsReducer(state = initialCellsState.contents, action: Actions): NormalizedNotebookCellArray['contents'] {
  switch (action.type) {
    case CellActions.Type.AddCellSuccess:
      return state.set(action.cellResource.token, action.cellResource.content);
    case CellActions.Type.EditCellContent:
      return state.set(action.cellToken, action.content);
    case CellActions.Type.UpdateCellFailure:
      return state.set(action.cellToken, action.original.content);
    case CellActions.Type.DestroyCellSuccess:
      return state.remove(action.cellToken);
    case CellActions.Type.LoadNotebookCellsSuccess:
      return state.merge(action.cellResources.map<[string, string]>((cell) => [cell.token, cell.content]));
  }

  return state;
}

function frozenReducer(state = initialCellsState.frozen, action: Actions): NormalizedNotebookCellArray['frozen'] {
  switch (action.type) {
    case CellActions.Type.AddCellSuccess:
      return state.set(action.cellResource.token, action.cellResource.frozen_in_layout);
    case CellActions.Type.ToggleFreezeCell:
      return state.set(action.cellToken, !state.get(action.cellToken));
    case CellActions.Type.DestroyCellSuccess:
      return state.remove(action.cellToken);
    case CellActions.Type.LoadNotebookCellsSuccess:
      return state.merge(action.cellResources.map<[string, boolean]>((cell) => [cell.token, cell.frozen_in_layout]));
    case CellActions.Type.UpdateContentType: // switching content type should unfreeze the cell
      return state.set(action.cellToken, false);
    case CellActions.Type.UpdateContentTypeFailure:
      return state.set(action.cellToken, action.original.frozen_in_layout);
  }

  return state;
}

function foldedReducer(state = initialCellsState.frozen, action: Actions): NormalizedNotebookCellArray['frozen'] {
  switch (action.type) {
    case CellActions.Type.FoldCell:
      return state.set(action.cellToken, !state.get(action.cellToken));
    case CellActions.Type.Select:
      return state.set(action.cellToken, false);
  }

  return state;
}

function contentTypesReducer(
  state = initialCellsState.contentTypes,
  action: Actions
): NormalizedNotebookCellArray['contentTypes'] {
  switch (action.type) {
    case CellActions.Type.AddCellSuccess:
      return state.set(action.cellResource.token, action.cellResource.content_type);
    case CellActions.Type.DestroyCellSuccess:
      return state.remove(action.cellToken);
    case CellActions.Type.LoadNotebookCellsSuccess:
      return state.merge(
        action.cellResources.map<[string, NotebookTypes.ContentType]>((cell) => [cell.token, cell.content_type])
      );
    case CellActions.Type.UpdateContentType:
      return state.set(action.cellToken, action.contentType);
    case CellActions.Type.UpdateContentTypeFailure:
      return state.set(action.cellToken, action.original.content_type);
  }

  return state;
}

function resourcesReducer(
  state = initialCellsState.resources,
  action: Actions
): NormalizedNotebookCellArray['resources'] {
  switch (action.type) {
    case CellActions.Type.AddCellSuccess:
      return state.set(action.cellResource.token, action.cellResource);
    case CellActions.Type.DestroyCellSuccess:
      return state.remove(action.cellToken);
    case CellActions.Type.LoadNotebookCellsSuccess:
      return state.merge(action.cellResources.map<[string, NotebookCellResource]>((cell) => [cell.token, cell]));
    case CellActions.Type.UpdateCellSuccess:
      return state.set(action.cellResource.token, action.cellResource);
    case CellActions.Type.ExportCellComplete: {
      const updatedCellResource = state.get(action.cellToken);
      // To re-render from the reducer, we need to create a new reference.
      // Object.create ensures we preserve the prototype of the original resource.
      const newResource = Object.assign(
        Object.create(Object.getPrototypeOf(updatedCellResource)),
        updatedCellResource,
        { local_dataset_exported_at: action.localDatasetExportedAt }
      );
      // TODO: Remove the HAL-related reliance in the future, as this pattern depends on HAL methods.
      return state.set(action.cellToken, newResource);
    }
    case CellActions.Type.ExportedCellDelete: {
      const updatedCellResource = state.get(action.cellToken);
      const newResource = {
        ...updatedCellResource,
        local_dataset_exported_at: null,
      };
      Object.setPrototypeOf(newResource, Object.getPrototypeOf(updatedCellResource));
      return state.set(action.cellToken, newResource);
    }
  }

  return state;
}

function createdAtReducer(
  state = initialCellsState.createdAt,
  action: Actions
): NormalizedNotebookCellArray['createdAt'] {
  switch (action.type) {
    case CellActions.Type.AddCellSuccess:
      return state.set(action.cellResource.token, action.cellResource.created_at);
    case CellActions.Type.DestroyCellSuccess:
      return state.remove(action.cellToken);
    case CellActions.Type.LoadNotebookCellsSuccess:
      return state.merge(action.cellResources.map<[string, Date]>((cell) => [cell.token, cell.created_at]));
  }

  return state;
}

function executingReducer(
  state = initialCellsState.executing,
  action: Actions
): NormalizedNotebookCellArray['executing'] {
  switch (action.type) {
    case CellActions.Type.AddCellSuccess:
      return state.set(action.cellResource.token, false);
    case CellActions.Type.DestroyCellSuccess:
      return state.remove(action.cellToken);
    case CellActions.Type.LoadNotebookCellsSuccess:
      return state.merge(action.cellResources.map<[string, boolean]>((cell) => [cell.token, false]));
    case NotebookActions.Type.Run:
      return state.merge(
        action.cellTokensToRun.filter((token) => state.has(token)).map<[string, boolean]>((t) => [t, true])
      );
    case CellRunActions.Type.LoadCellRunResultSuccess:
      return state.set(action.cellToken, false);
    case CellActions.Type.RunFailed: // falls through
    case CellActions.Type.Idle:
      return state.set(action.cellToken, false);
    case NotebookActions.Type.InitSession: // falls through
    case NotebookActions.Type.RunFailed: // falls through
    case NotebookActions.Type.CancelRun: // falls through
    case NotebookActions.Type.Disconnected: // falls through
    case NotebookActions.Type.Terminated:
      return state.some((val) => val) ? state.map((executing, key) => false) : state;
  }

  return state;
}

function lastRunTokensReducer(
  state = initialCellsState.lastRunTokens,
  action: Actions
): NormalizedNotebookCellArray['lastRunTokens'] {
  switch (action.type) {
    case CellActions.Type.DestroyCellSuccess:
      return state.remove(action.cellToken);
    case CellRunActions.Type.CreateRunSuccess: // falls through
    case CellRunActions.Type.LoadLastCellRunSuccess: // falls through
    case CellRunActions.Type.LoadCellRunFromBatchSuccess:
      return state.set(action.cellToken, action.resource.token);
  }

  return state;
}

function missingRunsReducer(
  state = initialCellsState.missingRuns,
  action: Actions
): NormalizedNotebookCellArray['missingRuns'] {
  switch (action.type) {
    case CellRunActions.Type.LoadLastCellRunFailure:
      if (action.error instanceof NoResourceError) {
        return state.add(action.cellToken);
      }
      break;
    case CellActions.Type.DestroyCellSuccess:
      return state.remove(action.cellToken);
  }

  return state;
}

export const cellsReducer: ReducerTree<AppState, NormalizedNotebookCellArray, Actions> = {
  tokens: tokensReducer,
  contents: contentsReducer,
  frozen: frozenReducer,
  folded: foldedReducer,
  contentTypes: contentTypesReducer,
  createdAt: createdAtReducer,
  resources: resourcesReducer,
  executing: executingReducer,
  lastRunTokens: lastRunTokensReducer,
  missingRuns: missingRunsReducer,
};
